import { useQuery } from "@tanstack/react-query";
import { getUserPendingMethods, getUserReviewById } from "api/booking.api";
import { getSimilarTeachers, getTeacherDetailsById } from "api/teacher.api.ts";
import { useFilters } from "context/FilterContext";
import { useEffect, useLayoutEffect } from "react";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import {
    mediaSizes,
    narrowContainerPaddingDesktop,
    narrowContainerPaddingMobile,
    narrowContainerWidth,
    narrowPageContainerStyles,
} from "ui/theme";
import { ClassInfo } from "./sections/ClassInfo";
import { ClassLocation } from "./sections/ClassLocation";
import { ClassOverview } from "./sections/ClassOverview";
import { ClassReview } from "./sections/ClassReview";
import { HeaderSection } from "./sections/HeaderSection";
import { MobileScheduleCard } from "./sections/MobileScheduleCard";
import { SimilarClasses } from "./sections/SimilarClasses";
import { BookingPopupProvider } from "./booking/BookingPopupContext";

const slides = Array.from({length: 3});

export const ClassDetail = () => {
    useLayoutEffect(() => {
        window.scrollTo({top: 0, left: 0});
    }, []);

    const {class_id} = useParams();
    const {filters} = useFilters();

    const {data} = useQuery({
        queryKey: ["getTeacherDetailsById", class_id],
        queryFn: () =>
            getTeacherDetailsById({
                id: class_id,
            }),
        enabled: !!class_id,
    });

    const classDetail = data?.data;

    const {data: pending} = useQuery({
        queryKey: ["getUserPendingMethods", classDetail?.teacher?.id],
        queryFn: () => getUserPendingMethods(classDetail?.teacher?.id),
        enabled: !!classDetail?.teacher?.id,
    });

    const {data: reviews} = useQuery({
        queryKey: ["getUserReviewById", class_id],
        queryFn: () => getUserReviewById({id: class_id, userType: "teacher"}),
        enabled: !!class_id,
    });

    const classTitle = classDetail?.teacher?.class?.title;
    const {data: similarTeachers} = useQuery({
        queryKey: ["getSimilarTeachers", classTitle],
        queryFn: () =>
            getSimilarTeachers({
                limit: 30,
                query: classTitle,
                online_type: filters?.online_type?.value,
                teacher_id: classDetail?.teacher?.id,
            }),
        staleTime: 10 * 1000,
        enabled: !!classTitle && !!classDetail?.teacher?.id,
    });

    const filteredSimilarTeachers =
        similarTeachers?.data.filter(
            (teacher) => teacher.id !== classDetail?.teacher?.id
        ) || [];

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [class_id]);

    return (
        <BookingPopupProvider classDetail={classDetail}>
        <Container>
            <HeaderSection
                classDetail={classDetail}
                reviews={reviews?.data?.teacher_reviews || []}
            />
            <MainRow>
                <ClassOverview
                    title={`Learn ${classDetail?.teacher?.class?.title || ""} with ${classDetail?.first_name || ""} `}
                    gallery={classDetail?.gallery || slides}
                    introVideoUrl={classDetail?.teacher?.intro_video_url}
                />
                <ClassInfo
                    classDetail={classDetail}
                    pendingData={{
                        hasPending: pending?.data,
                        message: pending?.message,
                    }}
                />
                {reviews?.data?.teacher_reviews?.length > 0 && (
                    <ClassReview
                        reviews={reviews.data.teacher_reviews}
                        rate={classDetail?.teacher?.rate}
                        reviewCount={classDetail?.teacher?.review}
                    />
                )}

                {filters?.online_type?.value === "online" ? null : (
                    <ClassLocation classDetail={classDetail}/>
                )}
                <MobileScheduleCard classDetail={classDetail}/>
            </MainRow>

            
        </Container>


        <SimilarClassesWrapper>
            <SimilarClasses similarClasses={filteredSimilarTeachers}/>
        </SimilarClassesWrapper>
        </BookingPopupProvider>
    );
};

const Container = styled.div`
    margin-top: 1rem;
    min-height: var(--page-height);
    ${narrowPageContainerStyles}
    padding-bottom: 1.4rem;
    font-family: var(--font-secondary);

    @media (width >= ${mediaSizes.md}) {
        margin-top: 0;
        padding-top: 2rem;
    }
`;

const MainRow = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2.5rem;
    margin-top: 1.5rem;
    @media (width >= ${mediaSizes.lg}) {
        display: grid;
        grid-template-columns: minmax(0, 57%) minmax(0, 1fr);
        column-gap: 2.5rem;
        align-items: start;
        margin-top: 1.5rem;
    }
`;

const SimilarClassesWrapper = styled.div`
    margin-left: max(
        ${narrowContainerPaddingMobile},
        calc((100vw - ${narrowContainerWidth}) / 2 + ${narrowContainerPaddingMobile})
    );
    margin-right: 0;

    @media (width >= ${mediaSizes.md}) {
        margin-left: max(
            ${narrowContainerPaddingDesktop},
            calc((100vw - ${narrowContainerWidth}) / 2 + ${narrowContainerPaddingDesktop})
        );
        
    }
`;

